import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import { qformat, DATE_FORMATS } from "views_shared/app/javascript/utils/dateFormatters";
import ActivityTimeline from "../shared/ActivityTimeline";
import MatchedRules from "./MatchedRules";
import UserVerificationActions from "./UserVerificationActions";
import {
  IP_SOURCE_LABELS,
  OUTCOME_BADGE,
  OUTCOME_LABELS,
  QUEUE_RESOURCE,
  RESOLVED_ELSEWHERE_LABELS,
  SUPABASE_URL,
  USER_TYPE_LABELS,
  VERDICT_BADGE,
  VERDICT_LABELS,
  humanize,
} from "./userVerificationApi";

function Decision({ decision, onReviewed }) {
  return (
    <div className="border rounded p-3 mb-2">
      <div className="d-flex justify-content-between align-items-start">
        <div>
          <span className={`badge ${OUTCOME_BADGE[decision.outcome] || "bg-secondary"} me-2`}>
            {OUTCOME_LABELS[decision.outcome] || humanize(decision.outcome)}
          </span>
          <span className="text-muted small">
            {humanize(decision.trigger)} check, {qformat(decision.created_at, DATE_FORMATS.timestamp)}
          </span>
          {decision.reason && <span className="text-muted small"> · {humanize(decision.reason)}</span>}
        </div>
        <UserVerificationActions decision={decision} onComplete={onReviewed} />
      </div>
      <div className="row small mt-2">
        <div className="col-md-4">
          IP: <code>{decision.ip || "—"}</code>
          {decision.ip_source && <span className="text-muted"> ({IP_SOURCE_LABELS[decision.ip_source]})</span>}
        </div>
        <div className="col-md-4">Country: {decision.country || "—"}</div>
        <div className="col-md-4">User type: {USER_TYPE_LABELS[decision.user_type] || "—"}</div>
      </div>
      <div className="mt-2">
        <MatchedRules rules={decision.matched_rules} inputsMissing={decision.inputs_missing} />
      </div>
      {decision.reasons?.length > 0 && (
        <ul className="small mb-0 mt-1">
          {decision.reasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      )}
      {decision.review_verdict && (
        <div className="small mt-2">
          Verdict:{" "}
          <span className={`badge ${VERDICT_BADGE[decision.review_verdict] || "bg-secondary"}`}>
            {VERDICT_LABELS[decision.review_verdict] || humanize(decision.review_verdict)}
          </span>
          {decision.reviewed_by_name && ` by ${decision.reviewed_by_name}`}
          {decision.reviewed_at && `, ${qformat(decision.reviewed_at, DATE_FORMATS.timestamp)}`}
          {decision.review_comment && <div className="fst-italic text-break mt-1">“{decision.review_comment}”</div>}
        </div>
      )}
      {!decision.review_verdict && decision.resolved_elsewhere && (
        <div className="small mt-2">
          <span className="badge bg-light text-dark border">
            {RESOLVED_ELSEWHERE_LABELS[decision.resolved_elsewhere]}
          </span>
        </div>
      )}
    </div>
  );
}

Decision.propTypes = {
  decision: PropTypes.shape({}).isRequired,
  onReviewed: PropTypes.func.isRequired,
};

// The automated user verification decisions for one user, on the admin Trust tab, with the
// activity timeline of the user and those decisions. The user comes first, so a comment typed
// into the timeline is about the user.
function UserVerificationSection({ userId, vetted = false, vettedNote = null, onReviewed }) {
  const [decisions, setDecisions] = useState(null);
  const [error, setError] = useState(null);
  const [loadCount, setLoadCount] = useState(0);

  const load = useCallback(() => {
    const params = new URLSearchParams({ account_user_id: `eq.${userId}`, order: "created_at.desc" });
    fetch(`${SUPABASE_URL()}/rest/v1/${QUEUE_RESOURCE}?${params}`, { headers: authenticatedHeadersHash() })
      .then((response) => {
        if (!response.ok) throw new Error("Could not load verification decisions.");
        return response.json();
      })
      .then((data) => {
        setDecisions(data);
        setError(null);
        setLoadCount((count) => count + 1);
      })
      .catch((err) => setError(err.message));
  }, [userId]);

  useEffect(() => load(), [load]);

  const timelineEntities = useMemo(
    () => [
      { type: "Account::User", id: userId },
      ...(decisions || []).map((decision) => ({ type: "Admin::UserVerificationDecision", id: String(decision.id) })),
    ],
    [userId, decisions],
  );

  return (
    <div className="card mb-4">
      <div className="card-header">
        <h5 className="mb-0">Verification</h5>
      </div>
      <div className="card-body">
        {vetted && (
          <div className="small mb-2">
            <span className="badge bg-success me-2">Vetted</span>
            {vettedNote || <span className="text-muted">No vetted note</span>}
          </div>
        )}
        {error && <div className="text-danger small">{error}</div>}
        {!error && decisions === null && <div className="text-muted small">Loading…</div>}
        {decisions?.length === 0 && <div className="text-muted small">No automated verification decisions.</div>}
        {decisions?.map((decision) => (
          <Decision
            key={decision.id}
            decision={decision}
            onReviewed={() => {
              load();
              onReviewed?.();
            }}
          />
        ))}
        {decisions && (
          <div className="mt-3">
            <ActivityTimeline key={loadCount} entities={timelineEntities} />
          </div>
        )}
      </div>
    </div>
  );
}

UserVerificationSection.propTypes = {
  userId: PropTypes.string.isRequired,
  vetted: PropTypes.bool,
  vettedNote: PropTypes.string,
  onReviewed: PropTypes.func,
};

export default UserVerificationSection;
